import { createClient, SupabaseClient, User, Session } from '@supabase/supabase-js';
import type {
  Organization,
  Team,
  QRPoint,
  Case,
  RiskLevel,
  Category,
  Subscription,
  SubscriptionTier,
  // supabase-js の User（認証アカウント）と名前が衝突するため別名で受ける。
  // AppUser は genbasafeai.users の行に対応するアプリ側のプロフィール。
  User as AppUser, OperatorUsageStats, OperatorRecentLogin,
  OrgInvite, OrgMember, PendingInvite } from '../types';
import { logger } from '../utils/logger';
import { logInfo, logError, logWarn } from './clientLogger';

// App identifier for multi-tenant architecture
export const APP_ID = 'genbasafeai' as const;

// Database Types (matches multi-tenant Supabase schema)
export interface Database {
  genbasafeai: {
    Tables: {
      organizations: {
        Row: DbOrganization;
        Insert: Omit<DbOrganization, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbOrganization, 'id' | 'app_id'>>;
      };
      teams: {
        Row: DbTeam;
        Insert: Omit<DbTeam, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbTeam, 'id' | 'app_id'>>;
      };
      qr_points: {
        Row: DbQRPoint;
        Insert: Omit<DbQRPoint, 'id' | 'created_at' | 'updated_at' | 'scans' | 'posts'>;
        Update: Partial<Omit<DbQRPoint, 'id' | 'app_id'>>;
      };
      cases: {
        Row: DbCase;
        Insert: Omit<DbCase, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbCase, 'id' | 'app_id'>>;
      };
      users: {
        Row: DbUser;
        Insert: Omit<DbUser, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbUser, 'id' | 'app_id' | 'auth_id'>>;
      };
      usage_counters: {
        Row: DbUsageCounter;
        Insert: Omit<DbUsageCounter, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbUsageCounter, 'id' | 'app_id'>>;
      };
    };
  };
  public: {
    Tables: {
      user_subscriptions: {
        Row: DbUserSubscription;
        Insert: Omit<DbUserSubscription, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<DbUserSubscription, 'id'>>;
      };
    };
  };
  core: {
    Tables: {
      apps: {
        Row: DbApp;
        Insert: Omit<DbApp, 'created_at'>;  // No updated_at in actual table
        Update: Partial<Omit<DbApp, 'id'>>;
      };
      app_members: {
        Row: DbAppMember;
        Insert: Omit<DbAppMember, 'id' | 'joined_at'>;  // Matches actual structure
        Update: Partial<Omit<DbAppMember, 'id'>>;
      };
    };
  };
}

// Database Row Types - Core Schema
// Note: Matches actual genbacompass core.apps table structure
interface DbApp {
  id: string;  // TEXT PRIMARY KEY (e.g., 'genbasafeai')
  slug: string;
  name: string;
  plan: 'free' | 'light' | 'standard' | 'enterprise';
  created_by: string;  // UUID NOT NULL
  created_at: string;
  // Note: No updated_at column in actual table
}

// Note: Matches actual genbacompass core.app_members table structure
interface DbAppMember {
  id: string;  // UUID PRIMARY KEY
  app_id: string;  // TEXT NOT NULL
  user_id: string;  // UUID NOT NULL
  role: 'admin' | 'leader' | 'member';  // TEXT NOT NULL
  joined_at: string;  // TIMESTAMP (not created_at/updated_at)
}

// Database Row Types - Public Schema (Stripe)
interface DbUserSubscription {
  id: string;
  user_id: string;
  app_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  tier: 'free' | 'light' | 'standard' | 'enterprise';
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'paused';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Database Row Types - GenbaSafeAI Schema
interface DbOrganization {
  id: string;
  app_id: string;
  name: string;
  plan: 'free' | 'light' | 'standard' | 'enterprise';
  qr_limit: number;
  qr_used: number;
  /** migration 027 で追加。既定 1。 */
  seats?: number | null;
  created_at: string;
  updated_at: string;
}

interface DbTeam {
  id: string;
  app_id: string;
  organization_id: string;
  name: string;
  description: string | null;
  /** migration 027 で追加。工事の終了日時。 */
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface DbQRPoint {
  id: string;
  app_id: string;
  organization_id: string;
  team_id: string | null;
  name: string;
  token: string;
  scans: number;
  posts: number;
  last_scan: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  equipment_info: string | null;
}

interface DbCase {
  id: string;
  app_id: string;
  organization_id: string;
  team_id: string;
  qr_point_id: string | null;
  title: string;
  body: string;
  original_text: string;
  risk_level: 'Low' | 'Medium' | 'High';
  category1: string;
  category2: string;
  lesson: string | null;
  status: 'open' | 'in_progress' | 'resolved';
  has_audio: boolean;
  image_url: string | null;
  viewer_token: string | null; // migration 017 で追加。anon フローでもう取れる前提
  created_at: string;
  updated_at: string;
}

interface DbUser {
  id: string;
  app_id: string;
  auth_id: string;
  organization_id: string | null;
  email: string;
  name: string;
  role: 'admin' | 'leader' | 'member';
  created_at: string;
  updated_at: string;
  /** migration 027 で追加。既定表示する工事現場。null は「すべての現場」。 */
  default_team_id?: string | null;
}

interface DbUsageCounter {
  id: string;
  app_id: string;
  organization_id: string;
  counter_type: 'ai_analysis' | 'audio_transcription' | 'qr_scan';
  period_start: string;
  period_end: string;
  count: number;
  limit_value: number;
  created_at: string;
  updated_at: string;
}

// Singleton client instance
let supabaseClient: SupabaseClient<Database> | null = null;

/**
 * Get or create Supabase client instance
 */
export function getSupabase(): SupabaseClient<Database> | null {
  if (supabaseClient) return supabaseClient;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    logger.warn('Supabase credentials not found. Running in demo mode.');
    return null;
  }

  supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    db: {
      schema: 'genbasafeai' // Default schema for this app
    }
  });

  return supabaseClient;
}

/**
 * Get Supabase client for public schema (subscriptions)
 */
export function getSupabasePublic(): SupabaseClient<Database> | null {
  const client = getSupabase();
  if (!client) return null;

  // Create a new client with public schema
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    db: {
      schema: 'public'
    }
  });
}

/**
 * Check if Supabase is configured
 */
export function isSupabaseConfigured(): boolean {
  return getSupabase() !== null;
}

// ============ Auth Functions ============

// Provision new user with organization via Edge Function (bypasses RLS)
/* ── Edge Function のエラー本文を取り出す ──────────────────────────
   supabase-js の FunctionsError は種類で context の中身が違う（実測）。
     FunctionsHttpError  : context = Response（本文に { error, code } が入る）
     FunctionsFetchError : context = TypeError（リクエストが届いていない。CORS 等）
   context を Response と決め打ちして .body を文字列として読む実装は
   常に空振りし、日本語 UI に "Edge Function returned a non-2xx status code"
   という英語がそのまま出る。必ずこの関数を通すこと。 */
export async function extractFunctionError(
  error: unknown,
): Promise<{ message: string | null; code: string | null; status: number | null; reached: boolean }> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    const body = await ctx.clone().json().catch(() => null) as { error?: unknown; code?: unknown } | null;
    return {
      message: body?.error != null ? String(body.error) : null,
      code: body?.code != null ? String(body.code) : null,
      status: ctx.status,
      reached: true,
    };
  }
  return { message: null, code: null, status: null, reached: false };
}

/** 届かなかったとき（CORS・オフライン）に画面へ出す文言。原文の英語は出さない。 */
export const NETWORK_ERROR_MESSAGE = 'サーバーに接続できませんでした。通信環境をご確認ください。';

export async function provisionUser(opts?: {
  /** 招待を受け入れて、いまの組織のデータごと合流する */
  acceptInvite?: boolean;
  inviteId?: string;
}): Promise<{
  organization: Organization | null;
  user: DbUser | null;
  created: boolean;
  error: string | null;
  /** 別の組織から招待されている（本人の確認が必要） */
  pendingInvite: PendingInvite | null;
  /** 合流したときに移動した件数 */
  merged: { cases: number; qrPoints: number; teams: number } | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    return { organization: null, user: null, created: false, error: 'ただいまご利用いただけません。', pendingInvite: null, merged: null };
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { organization: null, user: null, created: false, error: 'ログインの有効期限が切れています。もう一度ログインしてください。', pendingInvite: null, merged: null };
  }

  try {
    const response = await supabase.functions.invoke('genbasafeai_provision-user', {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      },
      /* 本文は合流を実行するときだけ意味を持つ。通常の呼び出しでは
         サーバーが本文なしとして扱う。 */
      body: opts?.acceptInvite
        ? { acceptInvite: true, ...(opts.inviteId ? { inviteId: opts.inviteId } : {}) }
        : undefined,
    });

    if (response.error) {
      logger.error('Provision user error:', response.error);
      /* FunctionsHttpError は本文（{ error: '日本語' }）を context の Response に持つ。
         これを読まずに response.error.message を使うと、日本語 UI に
         "Edge Function returned a non-2xx status code" という英語が出る。 */
      const { message } = await extractFunctionError(response.error);
      return {
        organization: null, user: null, created: false,
        error: message ?? NETWORK_ERROR_MESSAGE, pendingInvite: null, merged: null,
      };
    }

    const data = response.data;
    if (!data) {
      logger.error('Provision user: empty response');
      return { organization: null, user: null, created: false, error: 'サーバーから応答がありませんでした。しばらくしてからお試しください。', pendingInvite: null, merged: null };
    }
    if (data.error) {
      logger.error('Provision user data error:', data.error);
      return { organization: null, user: null, created: false, error: data.error, pendingInvite: null, merged: null };
    }

    return {
      organization: data.organization ? mapDbOrganization(data.organization) : null,
      user: data.user || null,
      created: data.created || false,
      error: null,
      pendingInvite: (data.pendingInvite as PendingInvite | undefined) ?? null,
      merged: (data.merged as { cases: number; qrPoints: number; teams: number } | undefined) ?? null,
    };
  } catch (err) {
    logger.error('Provision user exception:', err);
    /* 例外の原文（英語）は画面に出さない。詳細はログにだけ残す。 */
    return { organization: null, user: null, created: false, error: 'ただいま処理できませんでした。しばらくしてからお試しください。', pendingInvite: null, merged: null };
  }
}

// MagicLink認証（パスワード不要）
export async function signInWithMagicLink(email: string): Promise<{
  success: boolean;
  error: Error | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, error: new Error('Supabase not configured') };
  }

  // Redirect to root - the app will detect auth tokens in the hash
  const redirectUrl = window.location.origin;

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectUrl,
      /* どのアプリ経由の登録かを残す。共有 Supabase プロジェクトでは auth.users が
         全アプリ共通なので、これが無いと「安全ポスト＋の登録者」を後から特定できない
         （実際に他アプリの利用者を自アプリの離脱と読み違える事故が起きた）。
         **認可には絶対に使わない**（user_metadata は本人が書き換えられる）。集計専用。 */
      data: { signup_app: APP_ID },
    },
  });

  return {
    success: !error,
    error: error as Error | null
  };
}

// Google OAuth 認証（共有 Supabase プロジェクトで Google プロバイダは有効化済み）
// signInWithOAuth は全画面リダイレクトを起こす。戻り後は detectSessionInUrl が
// ?code= を交換し onAuthStateChange('SIGNED_IN') が発火する。
/* ============================================================================
 * ログイン認証コード（6桁）
 *
 * マジックリンクが法人のメール環境で機能しない事象への対策。リンクは1回限り
 * 有効で、メールの安全性を検査する仕組みが本人より先に開くと使い切られる。
 * 実測でリンクを消費すると同じメールの6桁コードも otp_expired になることを
 * 確認しているため、**リンクとコードは別のメールに分ける**。
 *
 * コードの送信は Supabase の /auth/v1/otp ではなく専用の Edge Function を使う。
 * 理由は文面を安全ポスト＋専用にできること（Supabase のテンプレートは
 * 4アプリ共有で、リンクを外すと他アプリが壊れる）と、送信のレート制限を
 * 自分で持てること。
 *
 * 設計判断の記録: docs/decisions/20260821-login-otp-self-send.md
 * ========================================================================== */

/** 送信結果。retryAfter は秒。 */
export interface LoginCodeRequestResult {
  success: boolean;
  /** 画面にそのまま出せる日本語。原文のエラーメッセージは載せない。 */
  message: string | null;
  retryAfter?: number;
}

/** ログイン認証コードをメールで送る。 */
export async function requestLoginCode(email: string): Promise<LoginCodeRequestResult> {
  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, message: 'ただいまご利用いただけません。時間をおいてお試しください。' };
  }

  try {
    const { error } = await supabase.functions.invoke('genbasafeai_send-login-code', {
      body: { email: email.trim().toLowerCase() },
    });

    if (!error) return { success: true, message: null };

    /* FunctionsHttpError は status と本文を context に持つ。取り出せなくても
       throw せず一般的な文言に丸める（画面が固まるのを防ぐ）。 */
    let status = 0;
    let retryAfter: number | undefined;
    const ctx = (error as unknown as { context?: unknown }).context;
    if (ctx instanceof Response) {
      status = ctx.status;
      const body = await ctx.clone().json().catch(() => null) as { retryAfter?: unknown } | null;
      if (typeof body?.retryAfter === 'number') retryAfter = body.retryAfter;
    }

    /* ★ コードもメールアドレスもエラー原文も clientLogger に渡さない。
       clientLogger は sessionStorage 保存とサーバ送信の経路を持つ。 */
    logger.warn('requestLoginCode failed', { status });

    if (status === 429) {
      const mins = retryAfter && retryAfter > 90 ? Math.ceil(retryAfter / 60) : 0;
      return {
        success: false,
        retryAfter,
        message: mins
          ? `送信回数の上限に達しました。約${mins}分後に再度お試しください。`
          : '送信間隔が短すぎます。1分ほどおいてから再度お試しください。',
      };
    }
    if (status === 400) {
      return { success: false, message: 'メールアドレスの形式をご確認ください。' };
    }
    return { success: false, message: 'メールを送信できませんでした。時間をおいて再度お試しください。' };
  } catch {
    return { success: false, message: 'メールを送信できませんでした。時間をおいて再度お試しください。' };
  }
}

/**
 * 6桁コードを検証してログインする。
 *
 * 成功すると onAuthStateChange('SIGNED_IN') が発火し、App.tsx 側の既存経路で
 * プロフィール確保と画面遷移まで進む。呼び出し側で遷移させる必要はない。
 */
/* コード検証に失敗した回数（このタブの中で数える）。
   何回目の失敗かが分かると、「1回目で通った」と「何度も落ちた」を区別できる。 */
let verifyFailures = 0;

export async function verifyLoginCode(
  email: string,
  rawCode: string,
): Promise<{ success: boolean; message: string | null }> {
  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, message: 'ただいまご利用いただけません。時間をおいてお試しください。' };
  }

  /* 全角数字・空白・区切り文字を許容する。現場の方が「１２３ ４５６」の形で
     貼り付けても通るようにする。正規化はここに集約して全呼び出し元へ効かせる。 */
  const code = rawCode.normalize('NFKC').replace(/\D/g, '');
  if (code.length !== 6) {
    return { success: false, message: '6桁の数字を入力してください。' };
  }

  try {
    /* type は 'email'。'magiclink' では通らないことを本番APIで実測した。 */
    const { error } = await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token: code, type: 'email' });
    if (error) {
      /* エラー原文は画面に出さない。文言を1種類に固定することで
         アカウントの存在も判別させない。

         **ただしサーバーには「失敗した」ことを残す。**
         以前はここが logger.warn だけで、本番の logger.warn は何も出さない。
         そのため「コードが届かなかったのか、届いたが通らなかったのか」を
         後から切り分けられなかった（実際に顧客の問い合わせで詰まった）。

         メールアドレスもコードも入れない。回数と、失敗したという事実だけ。
         時刻で login_code_log（送信の記録）と突き合わせれば、
         「送ったのに検証されていない＝受信箱に見えていない」と
         「検証して落ちた＝古いコードを入れた等」を区別できる。 */
      verifyFailures += 1;
      logger.warn('verifyLoginCode rejected');
      void import('./errorReporter')
        .then((m) => m.reportError('flow', 'ログインコードの検証に失敗しました', `試行${verifyFailures}回目`))
        .catch(() => { /* 記録できなくてもログインの妨げにはしない */ });
      return {
        success: false,
        message: 'コードが正しくないか、有効期限が切れています。一番新しいメールのコードをお試しください。',
      };
    }
    return { success: true, message: null };
  } catch {
    return { success: false, message: 'ログインできませんでした。時間をおいて再度お試しください。' };
  }
}

/* ============================================================================
 * チームプラン（席）と工事現場
 *
 * 席数の強制と招待の作成は Edge Function（genbasafeai_org-members）が行う。
 * クライアントから org_invites に直接 INSERT できないのは、
 * 席数の上限をサーバー側でしか強制できないため（migration 027）。
 * ========================================================================== */

/** Edge Function の共通呼び出し。エラー文言はサーバーが返す日本語をそのまま使う。 */
async function callOrgMembers(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<{ success: boolean; message: string | null; data?: unknown; code?: string }> {
  const supabase = getSupabase();
  if (!supabase) return { success: false, message: 'ただいまご利用いただけません。' };
  try {
    const { data, error } = await supabase.functions.invoke('genbasafeai_org-members', {
      body: { action, ...payload },
    });
    if (!error) return { success: true, message: null, data };

    /* FunctionsHttpError は本文を context に持つ。サーバーが返す日本語の
       error をそのまま画面に出す（原文の英語を出さない）。 */
    const ex = await extractFunctionError(error);
    const message = ex.message ?? (ex.reached ? 'ただいま処理できませんでした。' : NETWORK_ERROR_MESSAGE);
    const code = ex.code ?? undefined;
    logger.warn('callOrgMembers failed', { action });
    return { success: false, message, code };
  } catch {
    return { success: false, message: 'ただいま処理できませんでした。' };
  }
}

/** メンバーを招待する（管理者のみ）。 */
export async function inviteMember(email: string, role: 'admin' | 'leader') {
  return callOrgMembers('invite', { email: email.trim().toLowerCase(), role });
}

/** 招待を取り消す。 */
export async function revokeInvite(inviteId: string) {
  return callOrgMembers('revoke_invite', { inviteId });
}

/** メンバーを外す。 */
export async function removeMember(memberId: string) {
  return callOrgMembers('remove_member', { memberId });
}

/** 役割を変える。 */
export async function setMemberRole(memberId: string, role: 'admin' | 'leader') {
  return callOrgMembers('set_role', { memberId, role });
}

/** 組織のメンバー一覧。RLS で自組織のみ見える。 */
export async function getOrgMembers(organizationId: string): Promise<OrgMember[]> {
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('users')
    .select('id, email, name, role, created_at')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true });
  if (error) {
    logger.error('getOrgMembers failed', error);
    return [];
  }
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: String(r.id),
    email: String(r.email),
    name: String(r.name),
    role: r.role === 'admin' ? 'admin' : 'leader',
    createdAt: String(r.created_at),
  }));
}

/** 有効な招待の一覧（pending と seat_blocked）。期限切れは除く。 */
export async function getPendingInvites(organizationId: string): Promise<OrgInvite[]> {
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('org_invites')
    .select('id, organization_id, email, role, status, expires_at, created_at')
    .eq('organization_id', organizationId)
    .in('status', ['pending', 'seat_blocked'])
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });
  if (error) {
    logger.error('getPendingInvites failed', error);
    return [];
  }
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: String(r.id),
    organizationId: String(r.organization_id),
    email: String(r.email),
    role: r.role === 'admin' ? 'admin' : 'leader',
    status: String(r.status) as OrgInvite['status'],
    expiresAt: String(r.expires_at),
    createdAt: String(r.created_at),
  }));
}

/** 席数を変える（契約者本人のみ）。反映は webhook 経由なので少し待つ必要がある。 */
export async function updateSeats(seats: number): Promise<{ success: boolean; message: string | null }> {
  const supabase = getSupabase();
  if (!supabase) return { success: false, message: 'ただいまご利用いただけません。' };
  try {
    const { error } = await supabase.functions.invoke('genbasafeai_update-seats', {
      body: { seats },
    });
    if (!error) return { success: true, message: null };
    const ex = await extractFunctionError(error);
    const message = ex.message ?? (ex.reached ? '席数を変更できませんでした。' : NETWORK_ERROR_MESSAGE);
    logger.warn('updateSeats failed');
    return { success: false, message };
  } catch {
    return { success: false, message: '席数を変更できませんでした。' };
  }
}

/* ── 工事現場 ────────────────────────────────────────────────── */

/** 既定で表示する工事現場を保存する。null は「すべての現場」。 */
export async function updateDefaultTeam(teamId: string | null): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user) return false;
  /* migration 027 で name と default_team_id だけ列単位で UPDATE を開けている。
     role や organization_id は書けない。 */
  const { error } = await supabase
    .from('users')
    .update({ default_team_id: teamId })
    .eq('auth_id', auth.user.id);
  if (error) {
    logger.error('updateDefaultTeam failed', error);
    return false;
  }
  return true;
}

/** 工事現場を終了する（アーカイブ）。削除ではないので過去の報告は残る。 */
export async function archiveTeam(teamId: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { error } = await supabase
    .from('teams')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', teamId);
  if (error) {
    logger.error('archiveTeam failed', error);
    return false;
  }
  return true;
}

/** 終了した工事現場を再開する。 */
export async function unarchiveTeam(teamId: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { error } = await supabase.from('teams').update({ archived_at: null }).eq('id', teamId);
  if (error) {
    logger.error('unarchiveTeam failed', error);
    return false;
  }
  return true;
}

/** 工事現場の名前を変える。 */
export async function renameTeam(teamId: string, name: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { error } = await supabase.from('teams').update({ name: name.trim() }).eq('id', teamId);
  if (error) {
    logger.error('renameTeam failed', error);
    return false;
  }
  return true;
}

/** QR に工事現場を紐づける（外すときは null）。 */
export async function setQRPointTeam(qrPointId: string, teamId: string | null): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { error } = await supabase.from('qr_points').update({ team_id: teamId }).eq('id', qrPointId);
  if (error) {
    logger.error('setQRPointTeam failed', error);
    return false;
  }
  return true;
}

export async function signInWithGoogle(): Promise<{
  success: boolean;
  error: Error | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, error: new Error('Supabase not configured') };
  }

  // Redirect back to root - App.tsx detects ?code=/#access_token in the callback
  const redirectUrl = window.location.origin;

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUrl,
      queryParams: { prompt: 'select_account' },
      /* signInWithOAuth は options.data を受け付けない（型で判明）。
         Google 経由の登録にアプリ名を残す手段が現状ない。
         判別は genbasafeai.users 行の有無（has_app_profile）で行う。 */
    },
  });

  return {
    success: !error,
    error: error as Error | null
  };
}

// Legacy: Password sign in (kept for compatibility)
export async function signIn(email: string, password: string): Promise<{
  user: User | null;
  session: Session | null;
  error: Error | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    return { user: null, session: null, error: new Error('Supabase not configured') };
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  return {
    user: data.user,
    session: data.session,
    error: error as Error | null
  };
}

// Legacy: Password sign up (kept for compatibility)
export async function signUp(email: string, password: string, name: string): Promise<{
  user: User | null;
  error: Error | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    return { user: null, error: new Error('Supabase not configured') };
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { name, app_id: APP_ID }
    }
  });

  return {
    user: data.user,
    error: error as Error | null
  };
}

export async function signOut(): Promise<{ error: Error | null }> {
  const supabase = getSupabase();
  if (!supabase) {
    return { error: new Error('Supabase not configured') };
  }

  const { error } = await supabase.auth.signOut();
  return { error: error as Error | null };
}

export async function getCurrentSession(): Promise<Session | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function getCurrentUser(): Promise<User | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data } = await supabase.auth.getUser();
  return data.user;
}

// ============ Subscription Functions ============

export async function getUserSubscription(userId: string): Promise<Subscription | null> {
  const supabase = getSupabasePublic();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('app_id', APP_ID)
    .single();

  if (error || !data) return null;

  return mapDbSubscription(data);
}

export async function createOrUpdateSubscription(
  userId: string,
  subscriptionData: Partial<Subscription>
): Promise<Subscription | null> {
  const supabase = getSupabasePublic();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('user_subscriptions')
    .upsert({
      user_id: userId,
      app_id: APP_ID,
      stripe_customer_id: subscriptionData.stripeCustomerId || null,
      stripe_subscription_id: subscriptionData.stripeSubscriptionId || null,
      tier: subscriptionData.tier || 'free',
      status: subscriptionData.status || 'active',
      current_period_start: subscriptionData.currentPeriodStart || null,
      current_period_end: subscriptionData.currentPeriodEnd || null,
      cancel_at_period_end: subscriptionData.cancelAtPeriodEnd || false,
      metadata: subscriptionData.metadata || {}
    }, {
      onConflict: 'user_id,app_id'
    })
    .select()
    .single();

  if (error || !data) return null;

  return mapDbSubscription(data);
}

// ============ Organization Functions ============

export async function getOrganization(id: string): Promise<Organization | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) return null;

  return mapDbOrganization(data);
}

export async function getOrganizationByUserId(authId: string): Promise<Organization | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('organization_id')
    .eq('auth_id', authId)
    .single();

  if (userError || !userData || !userData.organization_id) return null;

  return getOrganization(userData.organization_id);
}

// updateOrganizationPlan は意図的に削除した（migration 019 / 監査 C-1）。
//
// plan と qr_limit をクライアントから UPDATE できる関数が存在すること自体が、
// 「決済せずに AI 上限を 10 → 10,000 回に上げられる」経路だった。RLS
// "Admins can manage their organization" は FOR ALL・列制限なしなので、
// 自組織の admin であれば devtools から直接叩けてしまう。Stripe 側には何も
// 起きないため、課金と実費の乖離を検知する手段もない。
//
// プラン付与は genbasafeai_stripe-webhook（Stripe 署名検証済み・service_role）
// の一本化とする。DB 側でも organizations の plan / qr_limit は
// authenticated の UPDATE 対象外にしてある。
//
// 画面に出すプランは organizations の SELECT 結果を読むだけでよい。
// クライアントから書き戻す必要はない。

export async function updateOrganizationName(
  id: string,
  name: string
): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  const { error } = await supabase
    .from('organizations')
    .update({ name })
    .eq('id', id);

  return !error;
}

// ============ User Functions ============

export async function getUserProfile(authId: string): Promise<DbUser | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('auth_id', authId)
    .single();

  if (error || !data) return null;

  return data;
}

/**
 * genbasafeai.users の行をアプリ側の User に変換する。
 *
 * organizationId は AppUser の必須項目なので、組織未割当の行は User として成立しない。
 * 空文字で埋めると「所属不明のログイン済みユーザー」が生まれて組織スコープの読み込みが
 * 静かに空振りするため、null を返して呼び出し側に provision を促す。
 */
export function mapUserProfile(db: DbUser): AppUser | null {
  if (!db.organization_id) return null;
  return {
    id: db.id,
    email: db.email,
    name: db.name,
    organizationId: db.organization_id,
    // DB は member も持つが UI 側の型は admin | leader の2値。
    // 権限を広げる方向に丸めないよう、member は leader 扱いにする。
    role: db.role === 'admin' ? 'admin' : 'leader',
    defaultTeamId: db.default_team_id ?? null,
    createdAt: db.created_at
  };
}

/**
 * ログイン中ユーザーのアプリ側プロフィールと所属組織を解決する。
 *
 * ストアの isAuthenticated の供給元。users を1回だけ読み、organization_id が
 * あるときにのみ organizations を引くので、getOrganizationByUserId と重ねて
 * 呼ばない限り問い合わせは増えない。
 *
 * セッションが無い / プロフィール未作成（provision 前）の場合は user: null を返す。
 */
export async function getCurrentUserContext(): Promise<{
  user: AppUser | null;
  organization: Organization | null;
}> {
  const supabase = getSupabase();
  if (!supabase) return { user: null, organization: null };

  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) return { user: null, organization: null };

  const profile = await getUserProfile(authData.user.id);
  if (!profile) return { user: null, organization: null };

  const user = mapUserProfile(profile);
  const organization = profile.organization_id
    ? await getOrganization(profile.organization_id)
    : null;

  return { user, organization };
}

export async function createUserProfile(
  authId: string,
  email: string,
  name: string,
  organizationId?: string,
  role: 'admin' | 'leader' | 'member' = 'member'
): Promise<DbUser | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('users')
    .insert({
      app_id: APP_ID,
      auth_id: authId,
      email,
      name,
      organization_id: organizationId || null,
      role
    })
    .select()
    .single();

  if (error || !data) return null;

  return data;
}

// ============ Team Functions ============

export async function getTeams(organizationId: string): Promise<Team[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true });

  if (error || !data) return [];

  return data.map(mapDbTeam);
}

export async function createTeam(
  organizationId: string,
  name: string,
  description?: string
): Promise<Team | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('teams')
    .insert({
      app_id: APP_ID,
      organization_id: organizationId,
      name,
      description: description || null
    })
    .select()
    .single();

  if (error || !data) return null;

  return mapDbTeam(data);
}

// ============ QR Point Functions ============

export async function getQRPoints(organizationId: string): Promise<QRPoint[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('qr_points')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error || !data) return [];

  return data.map(mapDbQRPoint);
}

/**
 * 匿名ワーカーが報告する QR ポイントの、報告フローに必要な最小情報。
 *
 * QRPoint (管理画面用) とは別型にしている。token・scans・posts は匿名者に
 * 返さないので、`QRPoint` を返すふりをして空文字や 0 を詰めると
 * 「取得できているのに 0 件」と誤読される型になるため。
 */
export interface WorkerQRPoint {
  id: string;
  organizationId: string;
  teamId?: string;
  name: string;
  isActive: boolean;
  equipmentInfo?: string;
}

/**
 * QR トークンから報告先を 1 件解決する。匿名ワーカー導線の入口。
 *
 * anon には genbasafeai.qr_points の権限を与えない（migration 019 で REVOKE）。
 * 公開 anon キーだけで他組織の QR を token 込みで列挙・改ざんできてしまうため。
 * 代わりに SECURITY DEFINER の RPC を経由し、is_active な 1 行のうち
 * 報告フローが使う列だけを受け取る。token は返ってこない。
 *
 * 注: 本 RPC は `public` スキーマにある。クライアントのデフォルトは
 * `genbasafeai` スキーマ（getSupabase 参照）なので、明示的に `.schema('public')`
 * で呼ばないと PGRST202 になる。
 *
 * エラーは throw する（null に丸めない）。null は「そのトークンの QR は無効」、
 * throw は「通信・権限の問題」を意味し、呼び出し側で画面表示を出し分ける。
 * 掲示済みポスターは回収できないので、この区別を潰してはいけない。
 *
 * ただし「RPC がまだ存在しない」場合だけは例外で、旧経路（qr_points 直 SELECT）
 * にフォールバックする。理由は getQRPointByTokenViaTable のコメント参照。
 */
export async function getQRPointByToken(token: string): Promise<WorkerQRPoint | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data, error } = await supabase
    .schema('public')
    .rpc('get_qr_point_by_token', { p_token: token });

  if (error) {
    // 「関数が無い」以外（権限エラー・通信断など）は従来どおり throw。
    // 特に migration 019 Part 2 適用後は、旧経路が permission denied になるのが
    // 正しい姿なので、そこへ落とし込んで握り潰してはいけない。
    if (!isMissingFunctionError(error)) {
      logger.error('getQRPointByToken: RPC error', error);
      throw error;
    }

    // 適用漏れに気づけるよう、フォールバックは必ず大きく鳴らす（warn ではなく error）。
    logger.error(
      '[MIGRATION 019 UNAPPLIED?] public.get_qr_point_by_token が見つかりません。' +
      'migration 019 Part 1 が本番 DB に未適用のまま、新フロントエンドが' +
      'デプロイされた可能性が高い。qr_points への直接 SELECT にフォールバックして' +
      'QR スキャンを継続するが、これは一時しのぎ。Supabase Studio の SQL Editor で' +
      '019 Part 1 を実行すること（Part 2 はその後）。',
      error
    );

    return getQRPointByTokenViaTable(supabase, token);
  }

  // RPC は SETOF。0 件なら null、1 件あれば先頭を返す
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;

  return {
    id: row.id,
    organizationId: row.organization_id,
    teamId: row.team_id ?? undefined,
    name: row.name,
    isActive: row.is_active,
    equipmentInfo: row.equipment_info ?? undefined
  };
}

/**
 * PostgREST が「その関数はスキーマキャッシュに無い」と言っているかを判定する。
 *
 * PostgREST は未知の関数への RPC に HTTP 404 と
 *   { code: 'PGRST202',
 *     message: 'Could not find the function public.get_qr_point_by_token(p_token) in the schema cache' }
 * を返す。supabase-js はこれをそのまま error オブジェクトに載せる。
 * 素の PostgreSQL 由来の undefined_function (SQLSTATE 42883) が透過してくる
 * 経路もあるため両方を見る。code だけに依存すると PostgREST のバージョン差で
 * 取りこぼすので、メッセージ本文も併用する。
 *
 * ここで「権限エラー(42501)」を拾ってはいけない。019 Part 2 適用後に旧経路へ
 * フォールバックしても permission denied になるだけで、エラーを二重に隠すことになる。
 */
function isMissingFunctionError(error: { code?: string | null; message?: string | null } | null): boolean {
  if (!error) return false;
  if (error.code === 'PGRST202' || error.code === '42883') return true;
  const message = (error.message ?? '').toLowerCase();
  return message.includes('could not find the function');
}

/**
 * 旧経路: genbasafeai.qr_points を直接 SELECT して QR を解決する。
 *
 * migration 019 Part 1 が未適用の本番 DB に新フロントを先にデプロイしてしまった
 * 場合の安全網。これが無いと全 QR スキャンが「通信できませんでした」画面になり、
 * 掲示済みポスターからの報告が現場で一斉に止まる（ポスターは回収できない）。
 *
 * 019 Part 2 適用後は anon から qr_points の SELECT 権限が剥がれるため、この関数は
 * permission denied を throw する。それが正しい挙動なので握り潰さない。
 *
 * クエリは 019 以前の実装（select('*') + is_active フィルタ）をそのまま踏襲する。
 * 列を絞ると 014 未適用など古いスキーマで別のエラーになり得るため、
 * 安全網としての確実性を優先する。取得した token は呼び出し側に返さない。
 */
async function getQRPointByTokenViaTable(
  supabase: SupabaseClient<Database>,
  token: string
): Promise<WorkerQRPoint | null> {
  const { data, error } = await supabase
    .from('qr_points')
    .select('*')
    .eq('token', token)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    logger.error('getQRPointByToken: フォールバック(qr_points 直 SELECT)も失敗', error);
    throw error;
  }

  const row = data as {
    id: string;
    organization_id: string;
    team_id: string | null;
    name: string;
    equipment_info?: string | null;
    is_active: boolean;
  } | null;

  if (!row) return null;

  return {
    id: row.id,
    organizationId: row.organization_id,
    teamId: row.team_id ?? undefined,
    name: row.name,
    isActive: row.is_active,
    equipmentInfo: row.equipment_info ?? undefined
  };
}

export async function createQRPoint(
  organizationId: string,
  name: string,
  teamId?: string,
  equipmentInfo?: string
): Promise<QRPoint | null> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('QR Point creation failed: Supabase not configured');
    return null;
  }

  logger.log('createQRPoint called with:', { organizationId, name, teamId, equipmentInfo });

  const token = generateToken();
  logger.log('Generated token:', token);

  // Build insert data - equipment_info may not exist in DB yet
  const baseInsertData = {
    app_id: APP_ID,
    organization_id: organizationId,
    team_id: teamId || null,
    name,
    token,
    is_active: true
  };

  // Try with equipment_info first, fallback without it if column doesn't exist
  let data: DbQRPoint | null = null;
  let error: { message: string; details?: string; hint?: string; code?: string } | null = null;

  // First attempt: with equipment_info
  const insertDataWithEquipment = { ...baseInsertData, equipment_info: equipmentInfo || null };
  logger.log('Insert data (with equipment_info):', insertDataWithEquipment);

  const result1 = await supabase
    .from('qr_points')
    .insert(insertDataWithEquipment)
    .select()
    .single();

  if (result1.error) {
    // Check if error is due to missing column
    const errorMsg = result1.error.message || '';
    if (errorMsg.includes('equipment_info') || errorMsg.includes('column') || result1.error.code === '42703') {
      logger.warn('equipment_info column not found, retrying without it...');

      // Second attempt: without equipment_info
      const result2 = await supabase
        .from('qr_points')
        .insert(baseInsertData)
        .select()
        .single();

      data = result2.data;
      error = result2.error;

      if (!error) {
        logger.log('QR Point created without equipment_info (column pending migration)');
      }
    } else {
      error = result1.error;
    }
  } else {
    data = result1.data;
  }

  if (error) {
    logger.error('QR Point creation error:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    return null;
  }

  if (!data) {
    logger.error('QR Point creation returned no data');
    return null;
  }

  logger.log('QR Point created successfully:', data);

  // organizations.qr_used はここで更新しない。
  // クライアントに qr_used の UPDATE 権を持たせると qr_limit の枠を自分で
  // 0 に戻せてしまうため、migration 019 で DB トリガ
  // (trg_qr_points_sync_org_qr_used) が qr_points の実数から再計算する方式に
  // 移した。削除時に減算していなかった既存のずれもトリガ側で解消される。

  return mapDbQRPoint(data);
}

export async function updateQRPoint(
  id: string,
  updates: Partial<Pick<QRPoint, 'name' | 'teamId' | 'isActive' | 'equipmentInfo'>>
): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  const dbUpdates: Partial<DbQRPoint> = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
  if (updates.equipmentInfo !== undefined) dbUpdates.equipment_info = updates.equipmentInfo || null;

  const { error } = await supabase
    .from('qr_points')
    .update(dbUpdates)
    .eq('id', id);

  return !error;
}

export async function incrementQRPointScans(token: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  // RPC calls go to public schema routing functions which delegate to genbasafeai schema
  // See migration 010_public_rpc_routing.sql for routing function definitions
  const { error } = await supabase.rpc('increment_qr_scans', { qr_token: token });
  return !error;
}

export async function deleteQRPoint(id: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  const { error } = await supabase
    .from('qr_points')
    .delete()
    .eq('id', id);

  return !error;
}

// ============ Case Functions ============

export async function getCases(
  organizationId: string,
  options?: {
    teamId?: string;
    status?: Case['status'];
    riskLevel?: RiskLevel;
    limit?: number;
    offset?: number;
  }
): Promise<Case[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  let query = supabase
    .from('cases')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (options?.teamId) {
    query = query.eq('team_id', options.teamId);
  }
  if (options?.status) {
    query = query.eq('status', options.status);
  }
  if (options?.riskLevel) {
    query = query.eq('risk_level', options.riskLevel);
  }
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error } = await query;

  if (error || !data) return [];

  return data.map(mapDbCase);
}

// Store last DB error for UI display
let lastDbError: { message: string; code?: string; details?: string } | null = null;
export function getLastDbError() {
  return lastDbError;
}
export function clearLastDbError() {
  lastDbError = null;
}

export async function createCase(caseData: {
  organizationId: string;
  teamId?: string;
  qrPointId?: string;
  title: string;
  body: string;
  originalText: string;
  riskLevel: RiskLevel;
  category1: Category;
  category2: string;
  lesson?: string;
  hasAudio: boolean;
  imageUrl?: string;
}): Promise<Case | null> {
  const supabase = getSupabase();
  lastDbError = null;

  if (!supabase) {
    const errMsg = 'Supabase client not available';
    logger.error('createCase:', errMsg);
    logError('createCase: Supabase not available', {}, 'supabase');
    lastDbError = { message: errMsg };
    return null;
  }

  // 事前検証: organizationIdは必須
  if (!caseData.organizationId) {
    const errMsg = 'organizationIdが必要です';
    logger.error('createCase: Validation failed -', errMsg);
    logError('createCase: Missing organizationId', {}, 'supabase');
    lastDbError = { message: errMsg, code: 'VALIDATION_ERROR' };
    return null;
  }

  // teamIdが空の場合は警告（nullとして保存される）
  if (!caseData.teamId) {
    logger.warn('createCase: teamId is empty - will be saved as null');
  }

  logInfo('createCase: Attempting to create case', {
    organizationId: caseData.organizationId,
    teamId: caseData.teamId || '(empty)',
    qrPointId: caseData.qrPointId || '(empty)',
    title: caseData.title
  }, 'supabase');

  // migration 019 (C-3): cases への直接 INSERT はやめ、SECURITY DEFINER RPC を使う。
  //
  // 旧実装は `.from('cases').insert({...}).select().single()` で、PostgREST は
  // これを INSERT ... RETURNING に落とす。RETURNING は SELECT ポリシーの評価を
  // 伴うため、本番では「誰でも全組織のケースを読める」SELECT ポリシー
  // ("Public can view cases for stats") がこの導線を成立させてしまっていた。
  // そのポリシーを消すには、先に匿名から cases への直接アクセスを全廃する必要がある。
  //
  // RPC は public スキーマにある (クライアントの既定スキーマは genbasafeai なので
  // 明示的に .schema('public') を付けないと PGRST202 になる)。
  // app_id / status はサーバ側で固定するので送らない。
  const { data, error } = await supabase
    .schema('public')
    .rpc('create_case_via_qr', {
      p_organization_id: caseData.organizationId,
      p_team_id: caseData.teamId || null,
      p_qr_point_id: caseData.qrPointId || null,
      p_title: caseData.title,
      p_body: caseData.body,
      p_original_text: caseData.originalText,
      p_risk_level: caseData.riskLevel,
      p_category1: caseData.category1,
      p_category2: caseData.category2,
      p_lesson: caseData.lesson || null,
      p_has_audio: caseData.hasAudio,
      p_image_url: caseData.imageUrl || null
    });

  if (error) {
    const errDetails = {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    };
    logger.error('createCase: Database error', error);
    logError('createCase: Database error', errDetails, 'supabase');
    lastDbError = { message: error.message, code: error.code, details: error.details };
    return null;
  }

  // RETURNS TABLE の RPC なので supabase-js は配列で返す (0 件なら空配列)
  const row = (Array.isArray(data) ? data[0] : data) as DbCase | undefined | null;

  if (!row) {
    const errMsg = 'No data returned from insert';
    logger.error('createCase:', errMsg);
    logError('createCase: No data returned', {}, 'supabase');
    lastDbError = { message: errMsg };
    return null;
  }

  logInfo('createCase: Successfully created case', { id: row.id }, 'supabase');

  // Increment QR point post count
  if (caseData.qrPointId) {
    await supabase.rpc('increment_qr_posts', { qr_id: caseData.qrPointId });
  }

  return mapDbCase(row);
}

// Edge Function (genbasafeai_analyze-report) の checkUsageLimit と必ず一致させる。
const PLAN_AI_LIMITS: Record<string, number> = {
  free: 10,
  light: 100,
  standard: 1000,
  enterprise: 10000,
};


/**
 * 匿名の投稿者本人が viewer_token を使って自分の case を1件取得する。
 * RLS をバイパスする SECURITY DEFINER の RPC を呼び出し、組織内構造（team / QR
 * 点 / 担当者など）は返さない。migration 017 で導入。
 *
 * 注: 本RPCは migration 017 で `public` スキーマに作成されている。クライアントの
 * デフォルトは `genbasafeai` スキーマ（getSupabase 参照）なので、明示的に
 * `.schema('public')` で呼ばないと PGRST202 で関数が見つからない。RPC 失敗は
 * 「見つかりません」に丸めず例外として throw する（呼び出し側でハンドリング）。
 */
export async function getCaseByViewerToken(token: string): Promise<import('../types').CaseStatusView | null> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('getCaseByViewerToken: Supabase not initialized');
    throw new Error('SUPABASE_NOT_INITIALIZED');
  }

  if (!token || !/^[0-9a-f-]{36}$/i.test(token)) {
    // RPC を叩く前に簡易バリデーション。明らかに UUID ではない値は弾く。
    logger.warn('getCaseByViewerToken: invalid token format');
    return null;
  }

  const { data, error } = await supabase
    .schema('public')
    .rpc('get_case_by_viewer_token', { p_token: token });

  if (error) {
    logger.error('getCaseByViewerToken: RPC error', error);
    throw error;
  }

  // RPC は SETOF。0 件なら null、1 件あれば先頭を返す
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;

  // migration 018 以降は resolution 関連カラムも返ってくる。action_taken が存在すれば解決済み。
  const hasResolution =
    typeof row.resolution_action_taken === 'string' && row.resolution_action_taken.length > 0;

  return {
    id: row.id,
    title: row.title,
    body: row.body,
    riskLevel: row.risk_level as RiskLevel,
    category1: row.category1 as Category,
    category2: row.category2,
    lesson: row.lesson ?? null,
    imageUrl: row.image_url ?? null,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    resolution: hasResolution
      ? {
          actionTaken: row.resolution_action_taken,
          actionsChecklist: Array.isArray(row.resolution_actions_checklist)
            ? row.resolution_actions_checklist
            : [],
          effectiveness: row.resolution_effectiveness ?? 'Medium',
          preventionNote: row.resolution_prevention_note ?? null,
          resolvedAt: row.resolved_at,
        }
      : null,
  };
}

/**
 * 改善実施記録を保存し、cases.status を 'resolved' に更新する。
 * UPSERT（同じ case_id への再保存は overwrite）。migration 018 で導入。
 */
export async function saveCaseResolution(input: {
  caseId: string;
  organizationId: string;
  actionTaken: string;
  actionsChecklist: import('../types').ResolutionActionTag[];
  effectiveness: 'Low' | 'Medium' | 'High';
  preventionNote: string | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const supabase = getSupabase();
  if (!supabase) return { ok: false, error: 'Supabase not initialized' };

  const { error: upsertError } = await supabase
    .from('case_resolutions')
    .upsert(
      {
        case_id: input.caseId,
        organization_id: input.organizationId,
        action_taken: input.actionTaken,
        actions_checklist: input.actionsChecklist,
        effectiveness: input.effectiveness,
        prevention_note: input.preventionNote,
        resolved_at: new Date().toISOString(),
      },
      { onConflict: 'case_id' }
    );

  if (upsertError) {
    logger.error('saveCaseResolution: upsert error', upsertError);
    return { ok: false, error: upsertError.message };
  }

  const { error: caseError } = await supabase
    .from('cases')
    .update({ status: 'resolved' })
    .eq('id', input.caseId);

  if (caseError) {
    logger.error('saveCaseResolution: case status update error', caseError);
    return { ok: false, error: caseError.message };
  }

  return { ok: true };
}

export async function updateCaseStatus(
  id: string,
  status: Case['status']
): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  const { error } = await supabase
    .from('cases')
    .update({ status })
    .eq('id', id);

  return !error;
}

/**
 * lesson フィールドを更新 (4Mなぜなぜ分析結果の追記等で使用).
 * 既存 lesson を保持したい場合は呼び出し側で連結してから渡すこと.
 */
export async function updateCaseLesson(
  id: string,
  lesson: string
): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('updateCaseLesson: Supabase not configured');
    return false;
  }

  // .select() を付けて「実際に更新された行」を取得する。
  // RLS で対象行が見えない場合、error は null でも data が空になる (silent 0-row update)。
  // その場合も false を返して呼び出し側でエラー表示できるようにする。
  const { data, error } = await supabase
    .from('cases')
    .update({ lesson })
    .eq('id', id)
    .select('id');

  if (error) {
    logger.error('updateCaseLesson failed', error);
    return false;
  }
  if (!data || data.length === 0) {
    logger.error('updateCaseLesson: 0 行更新 (RLS で対象外 or id 不一致の可能性)', { id });
    return false;
  }
  return true;
}

// ============ Image Storage Functions ============

const STORAGE_BUCKET = 'case-photos';

/**
 * 直近の画像アップロード失敗理由。
 * 2025-12 以降、共有 Supabase 上の別アプリのストレージポリシー変更で匿名アップロードが
 * 全滅していたのに 8 か月間気付けなかった（呼び出し側が null を握り潰していた）。
 * 再発時に「なぜ落ちたか」を UI とログの両方に出せるよう理由を保持する。
 */
let lastImageUploadError: { message: string; stage: 'upload' | 'sign' | 'client' | 'exception' } | null = null;
export function getLastImageUploadError() {
  return lastImageUploadError;
}

export async function uploadCaseImage(
  organizationId: string,
  caseId: string,
  imageBlob: Blob
): Promise<string | null> {
  lastImageUploadError = null;
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('uploadCaseImage: Supabase not available');
    lastImageUploadError = { message: 'Supabase クライアントが初期化されていません', stage: 'client' };
    return null;
  }

  try {
    const filePath = `${organizationId}/${caseId}.jpg`;

    logger.log('uploadCaseImage: Uploading', { filePath, size: imageBlob.size });

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, imageBlob, {
        contentType: 'image/jpeg',
        upsert: true
      });

    if (uploadError) {
      logger.error('uploadCaseImage: Upload failed', uploadError);
      lastImageUploadError = { message: uploadError.message || String(uploadError), stage: 'upload' };
      return null;
    }

    // Get public URL (since bucket is private, we need signed URL)
    const { data: urlData } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry

    if (!urlData?.signedUrl) {
      logger.error('uploadCaseImage: Failed to get signed URL');
      lastImageUploadError = { message: '署名付きURLの発行に失敗しました', stage: 'sign' };
      return null;
    }

    logger.log('uploadCaseImage: Success', { filePath });
    return urlData.signedUrl;
  } catch (e) {
    logger.error('uploadCaseImage: Exception', e);
    lastImageUploadError = { message: e instanceof Error ? e.message : String(e), stage: 'exception' };
    return null;
  }
}

export async function getCaseImageUrl(
  organizationId: string,
  caseId: string
): Promise<string | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const filePath = `${organizationId}/${caseId}.jpg`;

    const { data } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(filePath, 60 * 60 * 24); // 24 hours

    return data?.signedUrl || null;
  } catch {
    return null;
  }
}

// Update case's image_url in database
export async function updateCaseImageUrl(
  caseId: string,
  imageUrl: string
): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('updateCaseImageUrl: Supabase not available');
    return false;
  }

  try {
    const { error } = await supabase
      .from('cases')
      .update({ image_url: imageUrl })
      .eq('id', caseId);

    if (error) {
      logger.error('updateCaseImageUrl: Update failed', error);
      return false;
    }

    logger.log('updateCaseImageUrl: Success', { caseId });
    return true;
  } catch (e) {
    logger.error('updateCaseImageUrl: Exception', e);
    return false;
  }
}

// Get monthly stats for an organization (for worker view)
/**
 * ワーカー画面の「今月の投稿 / 解決済み」カウンタ。匿名からも呼ばれる。
 *
 * migration 019 (C-3) で SECURITY DEFINER RPC に置き換えた。
 * 旧実装は `.from('cases').select('id, status', { count: 'exact' })` で、
 * 画面で使うのは件数 2 つだけなのに許可としては当月分の全行が読めていた
 * （本番の "Public can view cases for stats" ポリシーと組み合わさると
 *   匿名から他組織の報告本文まで到達できた）。
 * RPC は集計値 2 つだけを返し、ケースの行は 1 行も返さない。
 * 月初の判定は Asia/Tokyo（サーバ側）。従来はブラウザのローカル時刻だった。
 *
 * RPC が失敗しても throw せず 0 件に丸める。画面はカウンタが 0 になるだけ。
 */
export async function getOrganizationMonthlyStats(
  organizationId: string
): Promise<{ monthPosts: number; resolvedCount: number }> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.error('getOrganizationMonthlyStats: Supabase not configured');
    return { monthPosts: 0, resolvedCount: 0 };
  }

  if (!organizationId) {
    logger.warn('getOrganizationMonthlyStats: organizationId is empty');
    return { monthPosts: 0, resolvedCount: 0 };
  }

  logger.log('getOrganizationMonthlyStats: Fetching stats for org:', organizationId);

  try {
    const { data, error } = await supabase
      .schema('public')
      .rpc('get_org_monthly_case_stats', { p_organization_id: organizationId });

    if (error) {
      logger.error('getOrganizationMonthlyStats RPC error:', {
        code: error.code,
        message: error.message,
        hint: error.hint,
        organizationId
      });
      return { monthPosts: 0, resolvedCount: 0 };
    }

    // RETURNS TABLE なので配列で返る。集計関数なので必ず 1 行来るはず。
    const row = (Array.isArray(data) ? data[0] : data) as
      { month_posts: number | null; resolved_count: number | null } | undefined | null;

    if (!row) {
      logger.warn('getOrganizationMonthlyStats: RPC returned no row', { organizationId });
      return { monthPosts: 0, resolvedCount: 0 };
    }

    const monthPosts = row.month_posts ?? 0;
    const resolvedCount = row.resolved_count ?? 0;

    logger.log('getOrganizationMonthlyStats: Result', { monthPosts, resolvedCount, organizationId });

    return { monthPosts, resolvedCount };
  } catch (e) {
    logger.error('getOrganizationMonthlyStats exception:', e);
    return { monthPosts: 0, resolvedCount: 0 };
  }
}

// ============ Usage Counter Functions ============

export async function checkUsageLimit(
  organizationId: string,
  counterType: 'ai_analysis' | 'audio_transcription' | 'qr_scan'
): Promise<{ allowed: boolean; current: number; limit: number }> {
  const supabase = getSupabase();
  if (!supabase) return { allowed: true, current: 0, limit: 100 };

  // RPC calls go to public schema routing functions which delegate to genbasafeai schema
  const { data, error } = await supabase.rpc('check_and_increment_usage', {
    p_org_id: organizationId,
    p_counter_type: counterType
  });

  if (error) {
    logger.error('Usage check error:', error);
    return { allowed: false, current: 0, limit: 0 };
  }

  return { allowed: data === true, current: 0, limit: 0 };
}

// ============ Mapping Functions ============

function mapDbOrganization(db: DbOrganization): Organization {
  return {
    id: db.id,
    name: db.name,
    plan: db.plan,
    qrLimit: db.qr_limit,
    qrUsed: db.qr_used,
    /* migration 027 適用前のレスポンスや、列を select していない経路でも
       壊れないよう 1 に倒す。1席＝従来と同じ挙動。 */
    seats: Math.max(db.seats ?? 1, 1),
    createdAt: db.created_at
  };
}

function mapDbTeam(db: DbTeam): Team {
  return {
    id: db.id,
    organizationId: db.organization_id,
    name: db.name,
    description: db.description || undefined,
    archivedAt: db.archived_at ?? null,
    createdAt: db.created_at
  };
}

function mapDbQRPoint(db: DbQRPoint): QRPoint {
  return {
    id: db.id,
    organizationId: db.organization_id,
    teamId: db.team_id || undefined,
    name: db.name,
    token: db.token,
    scans: db.scans,
    posts: db.posts,
    lastScan: db.last_scan || undefined,
    createdAt: db.created_at,
    isActive: db.is_active,
    equipmentInfo: db.equipment_info || undefined
  };
}

function mapDbCase(db: DbCase): Case {
  return {
    id: db.id,
    organizationId: db.organization_id,
    title: db.title,
    body: db.body,
    originalText: db.original_text,
    riskLevel: db.risk_level as RiskLevel,
    category1: db.category1 as Category,
    category2: db.category2,
    lesson: db.lesson || undefined,
    createdAt: db.created_at,
    teamId: db.team_id,
    qrPointId: db.qr_point_id || undefined,
    status: db.status,
    hasAudio: db.has_audio,
    imageUrl: db.image_url || undefined,
    viewerToken: db.viewer_token || undefined,
  };
}

function mapDbSubscription(db: DbUserSubscription): Subscription {
  return {
    id: db.id,
    userId: db.user_id,
    appId: db.app_id,
    stripeCustomerId: db.stripe_customer_id || undefined,
    stripeSubscriptionId: db.stripe_subscription_id || undefined,
    tier: db.tier as SubscriptionTier,
    status: db.status,
    currentPeriodStart: db.current_period_start || undefined,
    currentPeriodEnd: db.current_period_end || undefined,
    cancelAtPeriodEnd: db.cancel_at_period_end,
    metadata: db.metadata,
    createdAt: db.created_at
  };
}

// ============ Utility Functions ============

function generateToken(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let token = '';
  for (let i = 0; i < 12; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

// ============ Usage Counter Functions ============

export interface UsageStats {
  aiAnalysis: { used: number; limit: number };
  audioTranscription: { used: number; limit: number };
}

export async function getUsageStats(organizationId: string): Promise<UsageStats> {
  const supabase = getSupabase();

  // Default limits by plan
  const defaultStats: UsageStats = {
    aiAnalysis: { used: 0, limit: 10 },
    audioTranscription: { used: 0, limit: 5 }
  };

  if (!supabase) {
    logger.warn('getUsageStats: Supabase not initialized');
    return defaultStats;
  }

  try {
    const today = new Date();
    // Use UTC to match Edge Functions (avoid timezone conversion issues)
    const year = today.getUTCFullYear();
    const month = String(today.getUTCMonth() + 1).padStart(2, '0');
    const periodStartStr = `${year}-${month}-01`;

    logger.log('getUsageStats: Fetching stats for org:', organizationId, 'period:', periodStartStr);

    // Get organization plan for limits
    const { data: org, error: orgError } = await supabase
      .schema('genbasafeai')
      .from('organizations')
      .select('plan')
      .eq('id', organizationId)
      .single();

    if (orgError) {
      logger.error('getUsageStats: Failed to get organization plan:', orgError);
    }

    const plan = org?.plan || 'free';

    // Plan limits
    const planLimits: Record<string, { ai: number; audio: number }> = {
      free: { ai: 10, audio: 5 },
      light: { ai: 100, audio: 50 },
      standard: { ai: 1000, audio: 500 },
      enterprise: { ai: 10000, audio: 5000 }
    };

    const limits = planLimits[plan] || planLimits.free;

    // Get current usage
    const { data: counters, error: countersError } = await supabase
      .schema('genbasafeai')
      .from('usage_counters')
      .select('counter_type, count')
      .eq('organization_id', organizationId)
      .eq('period_start', periodStartStr);

    if (countersError) {
      logger.error('getUsageStats: Failed to get usage counters:', countersError);
      // Return defaults with correct limits even if counters fail
      return {
        aiAnalysis: { used: 0, limit: limits.ai },
        audioTranscription: { used: 0, limit: limits.audio }
      };
    }

    logger.log('getUsageStats: Found counters:', counters?.length || 0, 'plan:', plan);

    const aiCounter = counters?.find(c => c.counter_type === 'ai_analysis');
    const audioCounter = counters?.find(c => c.counter_type === 'audio_transcription');

    return {
      aiAnalysis: {
        used: aiCounter?.count || 0,
        limit: limits.ai
      },
      audioTranscription: {
        used: audioCounter?.count || 0,
        limit: limits.audio
      }
    };
  } catch (e) {
    logger.error('getUsageStats: Exception:', e);
    return defaultStats;
  }
}
// ============ Analytics Functions ============

export interface AnalyticsData {
  summary: {
    organizationCount: number;
    userCount: number;
    teamCount: number;
    qrPointCount: number;
    caseCount: number;
    paidSubscriptionCount: number;
  };
  usageByType: {
    counterType: string;
    totalCount: number;
  }[];
  qrPointStats: {
    name: string;
    scans: number;
    posts: number;
    lastScan: string | null;
  }[];
  categoryDistribution: {
    category: string;
    count: number;
  }[];
  riskDistribution: {
    riskLevel: string;
    count: number;
    percentage: number;
  }[];
  dailyPosts: {
    date: string;
    count: number;
  }[];
  planDistribution: {
    plan: string;
    count: number;
  }[];
}

export async function getAnalyticsData(): Promise<AnalyticsData | null> {
  const supabase = getSupabase();
  if (!supabase) {
    logger.warn('getAnalyticsData: Supabase not initialized');
    return null;
  }

  try {
    // Summary counts
    const [orgsRes, usersRes, teamsRes, qrRes, casesRes, subsRes] = await Promise.all([
      supabase.from('genbasafeai.organizations').select('id', { count: 'exact', head: true }),
      supabase.from('genbasafeai.users').select('id', { count: 'exact', head: true }),
      supabase.from('genbasafeai.teams').select('id', { count: 'exact', head: true }),
      supabase.from('genbasafeai.qr_points').select('id', { count: 'exact', head: true }),
      supabase.from('genbasafeai.cases').select('id', { count: 'exact', head: true }),
      supabase.from('user_subscriptions').select('id', { count: 'exact', head: true }).neq('tier', 'free')
    ]);

    // Usage counters by type
    const { data: usageData } = await supabase
      .from('genbasafeai.usage_counters')
      .select('counter_type, count');

    const usageByType: { counterType: string; totalCount: number }[] = [];
    if (usageData) {
      const grouped = usageData.reduce((acc, item) => {
        acc[item.counter_type] = (acc[item.counter_type] || 0) + item.count;
        return acc;
      }, {} as Record<string, number>);

      for (const [counterType, totalCount] of Object.entries(grouped)) {
        usageByType.push({ counterType, totalCount });
      }
      usageByType.sort((a, b) => b.totalCount - a.totalCount);
    }

    // QR Point stats
    const { data: qrPointData } = await supabase
      .from('genbasafeai.qr_points')
      .select('name, scans, posts, last_scan')
      .order('scans', { ascending: false });

    const qrPointStats = (qrPointData || []).map(qr => ({
      name: qr.name,
      scans: qr.scans,
      posts: qr.posts,
      lastScan: qr.last_scan
    }));

    // Category distribution
    const { data: casesData } = await supabase
      .from('genbasafeai.cases')
      .select('category1');

    const categoryMap: Record<string, number> = {};
    (casesData || []).forEach(c => {
      categoryMap[c.category1] = (categoryMap[c.category1] || 0) + 1;
    });

    const categoryDistribution = Object.entries(categoryMap)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);

    // Risk distribution
    const { data: riskData } = await supabase
      .from('genbasafeai.cases')
      .select('risk_level');

    const riskMap: Record<string, number> = {};
    (riskData || []).forEach(c => {
      riskMap[c.risk_level] = (riskMap[c.risk_level] || 0) + 1;
    });

    const totalCases = riskData?.length || 1;
    const riskDistribution = Object.entries(riskMap)
      .map(([riskLevel, count]) => ({
        riskLevel,
        count,
        percentage: Math.round((count / totalCases) * 100 * 10) / 10
      }))
      .sort((a, b) => {
        const order = { 'High': 0, 'Medium': 1, 'Low': 2 };
        return (order[a.riskLevel as keyof typeof order] || 3) - (order[b.riskLevel as keyof typeof order] || 3);
      });

    // Daily posts (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: dailyData } = await supabase
      .from('genbasafeai.cases')
      .select('created_at')
      .gte('created_at', thirtyDaysAgo.toISOString());

    const dailyMap: Record<string, number> = {};
    (dailyData || []).forEach(c => {
      const date = c.created_at.split('T')[0];
      dailyMap[date] = (dailyMap[date] || 0) + 1;
    });

    const dailyPosts = Object.entries(dailyMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => b.date.localeCompare(a.date));

    // Plan distribution
    const { data: orgPlanData } = await supabase
      .from('genbasafeai.organizations')
      .select('plan');

    const planMap: Record<string, number> = {};
    (orgPlanData || []).forEach(o => {
      planMap[o.plan] = (planMap[o.plan] || 0) + 1;
    });

    const planDistribution = Object.entries(planMap)
      .map(([plan, count]) => ({ plan, count }))
      .sort((a, b) => {
        const order = { 'enterprise': 0, 'standard': 1, 'light': 2, 'free': 3 };
        return (order[a.plan as keyof typeof order] || 4) - (order[b.plan as keyof typeof order] || 4);
      });

    return {
      summary: {
        organizationCount: orgsRes.count || 0,
        userCount: usersRes.count || 0,
        teamCount: teamsRes.count || 0,
        qrPointCount: qrRes.count || 0,
        caseCount: casesRes.count || 0,
        paidSubscriptionCount: subsRes.count || 0
      },
      usageByType,
      qrPointStats,
      categoryDistribution,
      riskDistribution,
      dailyPosts,
      planDistribution
    };
  } catch (e) {
    logger.error('getAnalyticsData: Exception:', e);
    return null;
  }
}



// ============ Auth State Listener ============

export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
): () => void {
  const supabase = getSupabase();
  if (!supabase) {
    return () => {};
  }

  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return () => subscription.unsubscribe();
}


// ============================================================================
// 運営者向け 機能利用状況（migration 020）
// ============================================================================
// 認可は **RPC の中** で行われる。ここでの分岐は画面の出し分けのためだけで、
// 運営者以外がこの関数を呼んでも 42501 が返りデータは1バイトも来ない。

/** 呼び出し元が運営者かどうか。サイドバーの出し分け用（boolean 1bit のみ）。 */
export async function checkIsOperator(): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  try {
    /* getSupabase() は db.schema='genbasafeai' で作られているため、public の関数を
       呼ぶには .schema('public') を挟む必要がある（既存の public RPC と同じ流儀）。
       これを忘れて genbasafeai スキーマを見に行き、関数が無いエラーが
       「運営者でない」に化けてメニューが出なかった。 */
    const { data, error } = await supabase.schema('public').rpc('genbasafeai_is_operator');
    if (error) {
      /* 「運営者ではない」と「呼び出しが失敗した」を混ぜない。混ぜると今回のように
         原因が分からなくなる。安全側に false を返すが、失敗は error で残す。 */
      logger.error('checkIsOperator: RPC failed (これは「運営者でない」とは別物)', error);
      return false;
    }
    return data === true;
  } catch (e) {
    logger.warn('checkIsOperator threw', e);
    return false;
  }
}

/**
 * 機能利用状況の集計を取得する。
 * 運営者以外は 42501 が返るので、その場合は null を返して画面側で弾く。
 */
/* 戻り値は単一の形にする。判別可能ユニオンにすると、この repo は tsconfig で
   strict が有効でないため `res.ok` による絞り込みが効かず、呼び出し側で
   型エラーになる（実際になった）。素直な形のほうが安全。 */
export interface OperatorUsageResult {
  stats: OperatorUsageStats | null;
  /** 運営者でないことが確定した場合のみ true */
  forbidden: boolean;
  /** stats が null のときの利用者向け文言 */
  message: string | null;
}

export async function fetchOperatorUsageStats(): Promise<OperatorUsageResult> {
  const supabase = getSupabase();
  if (!supabase) {
    return { stats: null, forbidden: false, message: 'Supabase が設定されていません' };
  }
  try {
    const { data, error } = await supabase.schema('public').rpc('genbasafeai_operator_usage_stats');
    if (error) {
      // 42501 = insufficient_privilege。運営者でないケース。
      const forbidden = error.code === '42501';
      if (!forbidden) logger.error('fetchOperatorUsageStats failed', error);
      return {
        stats: null,
        forbidden,
        message: forbidden
          ? 'この画面は運営者のみが閲覧できます。'
          : '利用状況の取得に失敗しました。時間をおいて再度お試しください。',
      };
    }
    return { stats: data as OperatorUsageStats, forbidden: false, message: null };
  } catch (e) {
    logger.error('fetchOperatorUsageStats threw', e);
    return { stats: null, forbidden: false, message: '利用状況の取得に失敗しました。' };
  }
}


/** 最近ログインしたユーザーの一覧（運営者のみ）。
 *  メールアドレスを含むため集計RPCとは別関数にしている（認可も別に効く）。 */
export async function fetchOperatorRecentLogins(
  limit = 100,
): Promise<{ rows: OperatorRecentLogin[] | null; message: string | null }> {
  const supabase = getSupabase();
  if (!supabase) return { rows: null, message: 'Supabase が設定されていません' };
  try {
    // getSupabase() の既定スキーマは genbasafeai。public の関数には .schema('public') が必要。
    const { data, error } = await supabase
      .schema('public')
      .rpc('genbasafeai_operator_recent_logins', { p_limit: limit });
    if (error) {
      logger.error('fetchOperatorRecentLogins: RPC failed', error);
      return {
        rows: null,
        message:
          error.code === '42501'
            ? 'この一覧は運営者のみが閲覧できます。'
            : 'ログイン履歴を取得できませんでした。',
      };
    }
    return { rows: (data ?? []) as OperatorRecentLogin[], message: null };
  } catch (e) {
    logger.error('fetchOperatorRecentLogins threw', e);
    return { rows: null, message: 'ログイン履歴を取得できませんでした。' };
  }
}


/** 掲示ポスターの印刷ボタン押下を記録する（未ログインでも呼べる）。
 *
 *  送るのはテーマ・バリアントID・実QR/見本QRの3つだけ。
 *  **掲示者が入力した会社名・現場名・緊急連絡先・担当者は送らない**
 *  （RPC の引数に存在しないので構造的に送れない）。
 *  組織IDとログイン状態はサーバ側が auth.uid() から導出する。
 *
 *  計測の失敗で印刷を止めてはいけないので、常に解決する（例外を投げない）。
 *  ただし黙って捨てず error ログに残す（今セッションで何度も踏んだ「静かな失敗」を避ける）。 */
export async function logPosterPrint(args: {
  themeKey: string;
  variantId: string;
  qrKind: 'live' | 'sample';
}): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  try {
    // getSupabase() の既定スキーマは genbasafeai。public の関数には .schema('public') が必要。
    const { error } = await supabase.schema('public').rpc('genbasafeai_log_poster_print', {
      p_theme_key: args.themeKey,
      p_variant_id: args.variantId,
      p_qr_kind: args.qrKind,
    });
    if (error) logger.error('logPosterPrint: RPC failed', error);
  } catch (e) {
    logger.error('logPosterPrint threw', e);
  }
}
